import { useState } from 'react';
import { Alert, Button, Modal, Spin, Switch } from 'antd';
import { CheckOutlined, SlackOutlined } from '@ant-design/icons';
import colorTokens from '../../../constants/design_system/colors';
import { useOrgRoleInfo } from '../../BaseIndex/useOrgRoleInfo';
import {
  useGetSlackIdentity,
  useGetSlackInstallation,
  useLinkSlackIdentityMutation,
  useSetSlackDeliveryMutation,
  useUnlinkSlackIdentityMutation,
} from '../../../serverState/slack';

/**
 * Settings, Slack notifications - where a person connects their OWN accounts.
 *
 * Separate from Settings, Integrations on a permission boundary, not a cosmetic one.
 * Integrations binds an external account to the whole organization and is admin-only,
 * in the router and in the sidebar alike. Linking your own Slack account is something
 * any member does for themselves, so it cannot live behind that gate - and the
 * server's identity callback redirects here for exactly that reason.
 *
 * Everything on this page acts on the caller's own identity. The server takes the user
 * from the session and the organization from a MEMBER-guarded route param, so there is
 * no request this page could make on someone else's behalf.
 *
 * DESIGN: the same shell and card silhouette as its Settings siblings - `px-12
 * max-w-5xl mx-auto`, a `text-brand-grey-dark text-xl` title, bordered cards, and the
 * 84px header row the Integrations cards use. Tailwind preflight is disabled repo-wide,
 * so bordered elements need an explicit `border-solid`.
 */
const STATUS_TONES = {
  neutral: 'bg-neutral-200 text-neutral-800',
  green: 'bg-green-100 text-green-600',
  orange: 'bg-yellow-100 text-yellow-600',
};

const StatusLabel = ({ tone, children }: { tone: keyof typeof STATUS_TONES; children: React.ReactNode }) => (
  <span className={`flex items-center gap-1 rounded px-1 text-xs font-normal leading-4 ${STATUS_TONES[tone]}`}>
    {children}
  </span>
);

export default function NotificationsSettings() {
  const { activeOrganization } = useOrgRoleInfo();
  const organizationId = activeOrganization?.id ?? null;

  const installation = useGetSlackInstallation(organizationId);
  const identityStatus = useGetSlackIdentity(organizationId);
  const linkMutation = useLinkSlackIdentityMutation();
  const unlinkMutation = useUnlinkSlackIdentityMutation();
  const deliveryMutation = useSetSlackDeliveryMutation();

  const [error, setError] = useState<string | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const onLink = async () => {
    if (!organizationId) return;
    setError(null);
    try {
      const authorizeUrl = await linkMutation.mutateAsync(organizationId);
      // A full navigation, not a router push: the next hop is slack.com.
      window.location.href = authorizeUrl;
    } catch {
      setError('Could not start the Slack sign-in. Please try again.');
    }
  };

  const onUnlink = async () => {
    if (!organizationId) return;
    setError(null);
    try {
      await unlinkMutation.mutateAsync(organizationId);
    } catch {
      setError('Could not unlink your Slack account. Please try again.');
    } finally {
      setUnlinkOpen(false);
    }
  };

  const onToggleDelivery = async (enabled: boolean) => {
    if (!organizationId) return;
    setError(null);
    try {
      await deliveryMutation.mutateAsync({ organizationId, enabled });
    } catch {
      setError('Could not update your Slack delivery preference. Please try again.');
    }
  };

  const page = (children: React.ReactNode) => (
    <div className="mx-auto w-full max-w-5xl px-12">
      <div className="mb-6 mt-8 text-xl font-semibold leading-7 text-brand-grey-dark">Slack notifications</div>
      {children}
    </div>
  );

  // Both reads have to settle before the card can say anything true: the identity
  // answer alone cannot distinguish "you have not linked yet" from "there is no
  // workspace here to link to", and those need opposite copy.
  if (installation.isLoading || identityStatus.isLoading) {
    return page(
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  const workspaceConnected = installation.data?.connected === true ? installation.data : null;
  const identity = identityStatus.data?.identity ?? null;
  const discoveredSlackUserId = identityStatus.data?.discoveredSlackUserId ?? null;
  const linked = identity?.connected === true;

  return page(
    <>
      <p className="m-0 -mt-3 mb-6 text-sm leading-relaxed text-neutral-900">
        Choose where Taskonio reaches you outside the app.
      </p>

      {error && <Alert className="mb-4" type="error" showIcon={true} message={error} />}

      <section className="rounded-lg border border-solid border-neutral-300 p-6">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded border-[1.333px] border-solid border-neutral-300 bg-neutral-200">
            <SlackOutlined className="text-2xl text-brand-grey-dark" />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-sm font-medium leading-5 text-brand-grey-dark">Slack</h2>
              {linked && (
                <StatusLabel tone="green">
                  <CheckOutlined className="text-[12px]" />
                  Your account is linked
                </StatusLabel>
              )}
              {!linked && <StatusLabel tone="neutral">Not linked</StatusLabel>}
              {/* Set by the system, not by the user, so it is a separate state from an
                  account that is simply not linked - and one a toggle cannot fix. */}
              {linked && identity?.deliveryDisabledReason && <StatusLabel tone="orange">Delivery paused</StatusLabel>}
            </div>
            <p className="m-0 text-xs leading-4 text-brand-grey-dark">
              Get your Taskonio notifications as Slack direct messages.
            </p>
          </div>

          {workspaceConnected && !linked && (
            <Button
              type="primary"
              // Matches the call to action on Settings, Integrations: antd renders every
              // button at 400, so this one is heavier, and the blue is the project's own
              // `--color-blue-600` inline because antd's `.ant-btn-primary` background
              // wins on specificity against a class. Still a CSS variable, so it inverts
              // under `data-theme="dark"`.
              className="shrink-0 font-semibold"
              icon={<SlackOutlined />}
              loading={linkMutation.isPending}
              style={{ backgroundColor: colorTokens.blue[600], borderColor: colorTokens.blue[600] }}
              onClick={() => void onLink()}
            >
              Connect your Slack account
            </Button>
          )}

          {linked && (
            <Button className="shrink-0" disabled={unlinkMutation.isPending} onClick={() => setUnlinkOpen(true)}>
              Unlink
            </Button>
          )}
        </div>

        {identityStatus.isError || installation.isError ? (
          <div className="mt-6 border-0 border-t border-solid border-neutral-300 pt-6">
            <p className="m-0 text-sm text-neutral-900">Could not load your Slack status. Please try again.</p>
            <Button
              className="mt-3"
              onClick={() => {
                void installation.refetch();
                void identityStatus.refetch();
              }}
            >
              Retry
            </Button>
          </div>
        ) : !workspaceConnected ? (
          // Nothing here is actionable by this user: linking an account needs a workspace
          // to link it to, and only an admin can connect one. Say who can, rather than
          // showing a button that would 404 at the service layer.
          <p className="m-0 mt-6 border-0 border-t border-solid border-neutral-300 pt-6 text-sm leading-relaxed text-neutral-900">
            Nobody has connected a Slack workspace to this organization yet. An organization admin can do that under
            Settings, Integrations.
          </p>
        ) : linked ? (
          <div className="mt-6 border-0 border-t border-solid border-neutral-300 pt-6">
            <dl className="m-0 flex items-baseline justify-between gap-4">
              <dt className="m-0 shrink-0 text-sm text-neutral-900">Slack workspace</dt>
              <dd className="m-0 min-w-0 truncate text-sm font-medium text-brand-grey-dark">
                {workspaceConnected.teamName}
              </dd>
            </dl>
            {identity?.deliveryDisabledReason ? (
              // No switch here on purpose: a system-imposed pause is not something this
              // toggle can affect, and showing one next to it would invite the user to
              // flip a control that would not take effect.
              <Alert
                className="mt-4"
                type="warning"
                showIcon={true}
                message="Taskonio has paused Slack delivery for your account."
                description={`Reason: ${identity.deliveryDisabledReason}. Unlink and connect again once it is resolved.`}
              />
            ) : (
              <div className="mt-4 flex items-center justify-between gap-4">
                <span className="text-sm text-neutral-900">Send me Slack messages</span>
                <Switch
                  checked={identity?.enabled ?? false}
                  loading={deliveryMutation.isPending}
                  onChange={(checked) => void onToggleDelivery(checked)}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 border-0 border-t border-solid border-neutral-300 pt-6">
            <p className="m-0 text-sm leading-relaxed text-neutral-900">
              Taskonio will send you a direct message in{' '}
              <span className="font-medium">{workspaceConnected.teamName}</span>. You stay signed in to Slack as
              yourself, and Taskonio never posts as you.
            </p>
            {/* A nudge, not a claim: the server matched the email on this account
                against the workspace member list, which is a guess until the user
                actually signs in and Slack confirms who they are. */}
            {discoveredSlackUserId && (
              <p className="m-0 mt-3 text-sm leading-relaxed text-neutral-900">
                We found an account with your email address in that workspace. Connecting will confirm it is yours.
              </p>
            )}
          </div>
        )}
      </section>

      <Modal
        open={unlinkOpen}
        title="Unlink your Slack account?"
        okText="Yes, unlink"
        cancelText="Cancel"
        okButtonProps={{ danger: true, loading: unlinkMutation.isPending }}
        onOk={() => void onUnlink()}
        onCancel={() => setUnlinkOpen(false)}
      >
        <p className="m-0 text-sm leading-relaxed text-neutral-900">
          Taskonio will stop sending you Slack messages for this organization. Your notifications stay in the app and in
          your email digest. You can link again at any time.
        </p>
      </Modal>
    </>
  );
}
